if (!customElements.get('product-addon')) {
  customElements.define(
    'product-addon',
    class ProductAddon extends HTMLElement {
      constructor() {
        super();

        this.mainProductId = Number(this.dataset.mainProductId);
        this.mainVariantIds = new Set(
          (this.dataset.mainVariantIds || '')
            .split(',')
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        );
        this.lockedLabel = this.dataset.lockedLabel || 'Add main item first';
        this.requireMainProductInCart = this.dataset.requireMainProductInCart !== 'false';
        this.isMainProductInCart = this.dataset.mainProductInCart === 'true';
        this.buttons = [];
        this.variantSelects = [];
        this.onVariantSelectChange = this.onVariantSelectChange.bind(this);
      }

      connectedCallback() {
        this.buttons = [...this.querySelectorAll('[data-addon-add-button]')];
        this.variantSelects = [...this.querySelectorAll('[data-addon-variant-select]')];
        this.variantSelects.forEach((select) => {
          select.addEventListener('change', this.onVariantSelectChange);
        });
        this.updateButtonState();

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }
      }

      disconnectedCallback() {
        this.variantSelects.forEach((select) => {
          select.removeEventListener('change', this.onVariantSelectChange);
        });
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      onVariantSelectChange(event) {
        const select = event.currentTarget;
        const addonItem = select.closest('[data-product-addon-item]') || this;
        const selectedOption = select.selectedOptions[0];
        const variantInput = addonItem.querySelector('.product-add-on__form [name="id"]');
        const isAvailable = selectedOption?.dataset.addonVariantAvailable === 'true';
        const buttons = [...addonItem.querySelectorAll('[data-addon-add-button]')];

        if (variantInput) {
          variantInput.value = select.value;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        buttons.forEach((button) => {
          button.dataset.addonAvailable = `${isAvailable}`;
        });

        this.updateVariantPrice(selectedOption, addonItem);
        this.updateVariantImage(selectedOption, addonItem);
        this.updateButtonState();
      }

      updateVariantImage(selectedOption, addonItem = this) {
        const imageUrl = selectedOption?.dataset.addonVariantImage;
        if (!imageUrl) return;

        const media = addonItem.querySelector('.product-add-on__media');
        if (!media) return;

        let image = media.querySelector('.product-add-on__image');
        if (!image) {
          const placeholder = media.querySelector('.product-add-on__placeholder');
          if (!placeholder) return;

          image = document.createElement('img');
          image.className = 'product-add-on__image';
          image.loading = 'lazy';
          placeholder.replaceWith(image);
        }

        image.src = imageUrl;
        image.removeAttribute('srcset');
        image.alt = selectedOption.dataset.addonVariantImageAlt || '';
        media.dataset.addonVariantMediaId = selectedOption.dataset.addonVariantMediaId || '';
      }

      updateVariantPrice(selectedOption, addonItem = this) {
        const price = selectedOption?.dataset.addonVariantPrice;
        const priceContainer = addonItem.querySelector('[data-addon-price]');
        if (!price || !priceContainer) return;

        const priceItems = priceContainer.querySelectorAll('.price-item--regular, .price-item--sale.price-item--last');
        priceItems.forEach((priceItem) => {
          priceItem.textContent = price;
        });
      }

      onCartUpdate(event) {
        if (this.isFullCartState(event?.cartData)) {
          this.updateParentStateFromCartItems(event.cartData.items);
          this.updateButtonState();
          return;
        }

        if (event?.cartData?.items) {
          event.cartData.items.forEach((item) => this.updateParentStateFromLineItem(item));
          this.updateButtonState();
          return;
        }

        if (event?.cartData && this.isCartLineItem(event.cartData)) {
          if (this.isMainLineItem(event.cartData)) {
            this.isMainProductInCart = true;
          }
          this.updateParentStateFromLineItem(event.cartData);
          this.updateButtonState();
          return;
        }

        const productVariantId = Number(event?.productVariantId);
        if (Number.isFinite(productVariantId)) {
          this.updateParentStateFromVariantId(productVariantId);
        }

        if (Number.isFinite(productVariantId) && this.mainVariantIds.has(productVariantId)) {
          this.isMainProductInCart = true;
        }

        this.updateButtonState();
      }

      isMainLineItem(item) {
        const productId = Number(item.product_id);
        const variantId = Number(item.variant_id || item.id);

        return (
          (Number.isFinite(productId) && productId === this.mainProductId) ||
          (Number.isFinite(variantId) && this.mainVariantIds.has(variantId))
        );
      }

      isCartLineItem(item) {
        const productId = Number(item?.product_id);
        const variantId = Number(item?.variant_id || item?.id);

        return Number.isFinite(productId) || Number.isFinite(variantId);
      }

      updateButtonState() {
        const hasLockedButtons = this.buttons.some((button) => {
          const isAvailable = button.dataset.addonAvailable === 'true';
          return isAvailable && this.requireMainProductInCart && !this.isButtonParentInCart(button);
        });
        this.classList.toggle('is-locked', hasLockedButtons);

        this.buttons.forEach((button) => {
          const isAvailable = button.dataset.addonAvailable === 'true';
          const label = button.querySelector('[data-add-to-cart-label]');

          if (!isAvailable) {
            button.setAttribute('disabled', 'disabled');
            button.removeAttribute('aria-disabled');
            return;
          }

          if (!this.requireMainProductInCart || this.isButtonParentInCart(button)) {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            if (label && button.dataset.enabledLabel) label.textContent = button.dataset.enabledLabel;
            return;
          }

          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          if (label) label.textContent = this.lockedLabel;
        });
      }

      updateParentStateFromCartItems(items) {
        const productIds = new Set(
          items
            .map((item) => Number(item.product_id))
            .filter((id) => Number.isFinite(id))
        );
        const variantIds = new Set(
          items
            .map((item) => Number(item.variant_id || item.id))
            .filter((id) => Number.isFinite(id))
        );

        this.isMainProductInCart = items.some((item) => this.isMainLineItem(item));
        this.buttons.forEach((button) => {
          button.dataset.addonParentInCart = `${this.buttonMatchesCartIds(button, productIds, variantIds)}`;
        });
      }

      updateParentStateFromLineItem(item) {
        const productId = Number(item.product_id);
        const variantId = Number(item.variant_id || item.id);

        this.buttons.forEach((button) => {
          if (button.dataset.addonParentInCart === 'true') return;

          const parentProductIds = this.getButtonParentProductIds(button);
          const parentVariantIds = this.getButtonParentVariantIds(button);
          const matchesProduct = Number.isFinite(productId) && parentProductIds.has(productId);
          const matchesVariant = Number.isFinite(variantId) && parentVariantIds.has(variantId);

          if (matchesProduct || matchesVariant) button.dataset.addonParentInCart = 'true';
        });
      }

      updateParentStateFromVariantId(variantId) {
        this.buttons.forEach((button) => {
          if (button.dataset.addonParentInCart === 'true') return;
          if (this.getButtonParentVariantIds(button).has(variantId)) {
            button.dataset.addonParentInCart = 'true';
          }
        });
      }

      buttonMatchesCartIds(button, productIds, variantIds) {
        const parentProductIds = this.getButtonParentProductIds(button);
        const parentVariantIds = this.getButtonParentVariantIds(button);

        return (
          [...parentProductIds].some((id) => productIds.has(id)) ||
          [...parentVariantIds].some((id) => variantIds.has(id))
        );
      }

      isButtonParentInCart(button) {
        return button.dataset.addonParentInCart === 'true' || (!button.dataset.addonParentProductIds && this.isMainProductInCart);
      }

      getButtonParentProductIds(button) {
        const ids = this.parseIdSet(button.dataset.addonParentProductIds);
        if (ids.size) return ids;

        return Number.isFinite(this.mainProductId) ? new Set([this.mainProductId]) : new Set();
      }

      getButtonParentVariantIds(button) {
        const ids = this.parseIdSet(button.dataset.addonParentVariantIds);
        return ids.size ? ids : this.mainVariantIds;
      }

      parseIdSet(value) {
        return new Set(
          `${value || ''}`
            .split(',')
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        );
      }

      isFullCartState(cartState) {
        return (
          Array.isArray(cartState?.items) &&
          Number.isFinite(Number(cartState.item_count)) &&
          Number.isFinite(Number(cartState.total_price))
        );
      }
    }
  );
}

if (!customElements.get('addon-product-gate')) {
  customElements.define(
    'addon-product-gate',
    class AddonProductGate extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.parentProductIds = new Set(
          (this.dataset.parentProductIds || '')
            .split(',')
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        );
        this.parentVariantIds = new Set(
          (this.dataset.parentVariantIds || '')
            .split(',')
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        );
        this.isParentProductInCart = this.dataset.parentProductInCart === 'true';
        this.disabledLabel = this.dataset.disabledLabel || 'Add main item first';
      }

      connectedCallback() {
        this.buttons = [...this.querySelectorAll('[data-addon-parent-gated="true"]')];
        this.updateButtonState();

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onCartUpdate(event) {
        if (this.isFullCartState(event?.cartData)) {
          this.isParentProductInCart = event.cartData.items.some((item) => this.isKnownParentLineItem(item));
          this.updateButtonState();
          return;
        }

        if (event?.cartData?.items) {
          const hasKnownParent = event.cartData.items.some((item) => this.isKnownParentLineItem(item));
          if (hasKnownParent) {
            this.isParentProductInCart = true;
            this.updateButtonState();
          }
          return;
        }

        if (event?.cartData && this.isKnownParentLineItem(event.cartData)) {
          this.isParentProductInCart = true;
          this.updateButtonState();
          return;
        }

        const productVariantId = Number(event?.productVariantId || event?.variantId);
        if (Number.isFinite(productVariantId) && this.parentVariantIds.has(productVariantId)) {
          this.isParentProductInCart = true;
          this.updateButtonState();
        }
      }

      onVariantChange(event) {
        const eventSectionId = event?.data?.sectionId || event?.sectionId;
        if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) return;

        if (event?.data?.variant) {
          this.buttons.forEach((button) => {
            button.dataset.addonParentVariantAvailable = `${event.data.variant.available}`;
          });
        }

        window.requestAnimationFrame(() => this.updateButtonState());
      }

      isKnownParentLineItem(item) {
        const productId = Number(item.product_id);
        const variantId = Number(item.variant_id || item.id);

        return (
          (Number.isFinite(productId) && this.parentProductIds.has(productId)) ||
          (Number.isFinite(variantId) && this.parentVariantIds.has(variantId))
        );
      }

      isFullCartState(cartState) {
        return (
          Array.isArray(cartState?.items) &&
          Number.isFinite(Number(cartState.item_count)) &&
          Number.isFinite(Number(cartState.total_price))
        );
      }

      updateButtonState() {
        this.buttons.forEach((button) => {
          const label = button.querySelector('[data-add-to-cart-label]');
          const isVariantAvailable = button.dataset.addonParentVariantAvailable !== 'false';

          button.dataset.addonParentInCart = `${this.isParentProductInCart}`;

          if (!this.isParentProductInCart) {
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
            button.dataset.addonParentGateApplied = 'true';
            if (label) label.textContent = button.dataset.addonParentDisabledLabel || this.disabledLabel;
            return;
          }

          if (!isVariantAvailable) {
            button.setAttribute('disabled', 'disabled');
            button.removeAttribute('aria-disabled');
            if (label) label.textContent = window.variantStrings?.soldOut || 'Sold out';
            return;
          }

          if (button.dataset.addonParentGateApplied === 'true') {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.dataset.addonParentGateApplied = 'false';
            if (label) label.textContent = button.dataset.addonParentEnabledLabel || window.variantStrings?.addToCart || 'Add to cart';
          }
        });
      }
    }
  );
}
